import { useRef, useCallback, useEffect } from 'react';
import { useDispatch } from 'react-redux';
import * as Sentry from '@sentry/react';
import store, { AppDispatch, useSelector } from 'store';
import api, { apiUrl, TAG_TYPES } from 'store/api';
import { invalidateCareCircleTags } from 'store/api/careCircle';
import { invalidateCareProfileTags } from 'store/api/careProfile';
import {
  invalidateNotificationTags,
  notificationApi,
} from 'store/api/notification';
import { invalidateChatSessionTags } from 'store/api/chatSession';
import { invalidateShortlistTags } from 'store/api/shortlist';
import { inferenceApi } from 'store/api/inference';
import { invalidateBacktestTags } from 'store/api/evaluation';
import { organiseApi } from 'store/api/organise';
import { organiseDocumentApi } from 'store/api/organiseDocument';
import {
  documentProcessed,
  messageProcessed,
} from 'store/slices/onboardingV3Slice';
import { answersRejected } from 'store/slices/benefitsCalculatorSlice';
import { refetchEligibilities } from 'store/slices/careCircleSlice';
import { accountNameUpdated, invalidateAccount } from 'store/slices/iamSlice';
import { newToast } from 'store/slices/uiSlice';
import { benefitsMap } from 'config/benefitsData';

export interface Event {
  eventID: string;
  eventIndex: number;
  publishedAt: string;
  eventType: string;
  /** JSON; the API forwards only events whose payload is a string. */
  payload: string;
}

type PayloadFields = Record<string, unknown>;

export interface EventHandler {
  eventFilter: (event: Event) => boolean;
  handler: (event: Event) => void;
}

// Every backtest event starts with this, as the API sends each one to admins.
export const BACKTEST_EVENT_PREFIX = 'EvaluationBacktest';

type TagType = (typeof TAG_TYPES)[number];

const RECONNECT_EXCLUDED_TAGS: TagType[] = [
  // The refetch replaces the current care circle, and clears it if it fails.
  'CareCircles',
  // Refetched through refetchEligibilities, which shows a loading state.
  'Eligibilities',
];

const RECONNECT_REFETCHED_TAGS = TAG_TYPES.filter(
  (tag) => !RECONNECT_EXCLUDED_TAGS.includes(tag),
);

// Constants for exponential backoff
const INITIAL_RETRY_DELAY_MS = 1000; // 1 second
const MAX_RETRY_DELAY_MS = 30000; // 30 seconds
const BACKOFF_MULTIPLIER = 2;

/**
 * Extracts an ID from a WebSocket event payload.
 * Handles both tagged (careCircleID) and untagged (CareCircleID) JSON keys.
 */
const parsePayloadID = (payload: string, key: string): string | null => {
  const untaggedKey = key.charAt(0).toUpperCase() + key.slice(1);
  try {
    const parsed: PayloadFields = JSON.parse(payload);
    const id = parsed[key] || parsed[untaggedKey];
    return typeof id === 'string' ? id : null;
  } catch {
    // A payload that isn't JSON carries no ID; the caller treats it as absent.
    return null;
  }
};

const parseCalendarID = (payload: string) =>
  parsePayloadID(payload, 'calendarID');
const parseCareCircleID = (payload: string) =>
  parsePayloadID(payload, 'careCircleID');
const parseDocumentID = (payload: string) =>
  parsePayloadID(payload, 'documentID');
const parseMessageID = (payload: string) =>
  parsePayloadID(payload, 'messageID');

/**
 * Extracts the backtest ID from a WebSocket event payload.
 */
const parseBacktestID = (payload: unknown): string | null => {
  if (typeof payload !== 'string') return null;
  try {
    return JSON.parse(payload).backtestID || null;
  } catch {
    // Without an id the list still refreshes, so there is nothing to report.
    return null;
  }
};

/**
 * Returns true if the event's care circle matches the one currently being
 * viewed (or if either side is unknown, in which case we don't filter).
 */
const isCurrentCareCircle = (payload: string): boolean => {
  const currentID = store.getState().careCircle.currentCareCircle?.id;
  if (!currentID) return true;
  const eventID = parseCareCircleID(payload);
  if (!eventID) return true;
  return eventID === currentID;
};

/**
 * Returns true if the event was triggered by the current user's own action.
 * Checks both untagged (ByAccountID) and tagged (updatedByID) payload keys.
 * When true, the mutation's own invalidatesTags already handled cache
 * invalidation, so the WebSocket-triggered invalidation is redundant.
 */
const isOwnEvent = (payload: string): boolean => {
  const accountID = store.getState().iam.account?.id;
  if (!accountID) return false;
  try {
    const parsed: PayloadFields = JSON.parse(payload);
    const byID = parsed.ByAccountID || parsed.updatedByID || null;
    return byID === accountID;
  } catch {
    return false;
  }
};

type TokenGetter = () => Promise<string | undefined>;

// The account's care circles as one comparable string; null until they've
// loaded (or while a refetch of them has failed).
const careCircleIDsOf = (circles: { id: string }[] | null | undefined) =>
  circles
    ?.map((c) => c.id)
    .sort()
    .join(',') ?? null;

export const useEventStream = () => {
  const dispatch = useDispatch<AppDispatch>();
  const abortRef = useRef<AbortController | null>(null);
  const reconnectTimeoutRef = useRef<NodeJS.Timeout | null>(null);
  const retryCountRef = useRef<number>(0);
  const getTokenRef = useRef<TokenGetter | null>(null);
  const tokenErrorReportedRef = useRef<boolean>(false);
  // Set when the stream reconnects for a reason of its own, so what it may have missed is fetched once it's back.
  const refetchOnConnectRef = useRef<boolean>(false);
  // The care circles the open stream was connected with. Set only in connect(), so
  // a failed refetch nulling the store can't make a later change look like a first load.
  const connectedCareCircleIDsRef = useRef<string | null>(null);

  const handlers: EventHandler[] = [
    {
      eventFilter: (event: Event) =>
        event.eventType === 'MemberRegisteredEventV1',
      handler: () => {
        console.log('[useEventStream] received MemberRegisteredEventV1');
        dispatch(invalidateAccount());
      },
    },
    {
      eventFilter: (event: Event) =>
        event.eventType === 'AccountNameUpdatedEventV1',
      handler: (event: Event) => {
        console.log('[useEventStream] received AccountNameUpdatedEventV1');
        const accountID = parsePayloadID(event.payload, 'accountID');
        if (!accountID) return;
        dispatch(
          accountNameUpdated({
            accountID,
            firstName: parsePayloadID(event.payload, 'firstName') ?? '',
            lastName: parsePayloadID(event.payload, 'lastName') ?? '',
          }),
        );
      },
    },
    {
      eventFilter: (event: Event) =>
        event.eventType === 'AccountUpgradedToOrgOwnerEventV1' ||
        event.eventType === 'AccountUpgradedToPartnerAdminEventV1',
      handler: (event: Event) => {
        console.log(`[useEventStream] received ${event.eventType}`);
        dispatch(invalidateAccount());
      },
    },
    {
      eventFilter: (event: Event) =>
        event.eventType === 'CareProfileAnswersUpdatedEventV1',
      handler: (event: Event) => {
        if (isOwnEvent(event.payload)) return;
        if (!isCurrentCareCircle(event.payload)) return;
        console.log(
          '[useEventStream] received CareProfileAnswersUpdatedEventV1',
          event.payload,
        );
        dispatch(invalidateCareProfileTags());
      },
    },
    {
      eventFilter: (event: Event) =>
        event.eventType === 'CareProfileChatAnswersRejectedEventV1',
      handler: (event: Event) => {
        if (!isCurrentCareCircle(event.payload)) return;
        console.log(`[useEventStream] received ${event.eventType}`);
        const messageId = parsePayloadID(event.payload, 'messageID');
        if (messageId) dispatch(answersRejected(messageId));
      },
    },
    {
      eventFilter: (event: Event) =>
        event.eventType === 'CareCircleMemberDetailsUpdatedEventV1',
      handler: (event: Event) => {
        if (!isCurrentCareCircle(event.payload)) return;
        const invalidate = () => {
          console.log(
            '[useEventStream] received CareCircleMemberDetailsUpdatedEventV1',
          );
          dispatch(invalidateCareCircleTags());
        };
        // Defer own events past the page transition animation (250ms)
        // to avoid re-renders mid-slide.
        if (isOwnEvent(event.payload)) {
          setTimeout(invalidate, 500);
        } else {
          invalidate();
        }
      },
    },
    {
      eventFilter: (event: Event) =>
        event.eventType === 'EligibilityCalculatedEventV1',
      handler: (event: Event) => {
        if (!isCurrentCareCircle(event.payload)) return;

        const payload = event.payload;
        if (!payload) return;
        if (typeof payload !== 'string') return;

        const eligibilityCalculatedEvent = JSON.parse(payload);
        if (typeof eligibilityCalculatedEvent !== 'object') return;
        if (!eligibilityCalculatedEvent.CareCircleID) return;

        console.log(
          '[useEventStream] received EligibilityCalculatedEventV1',
          eligibilityCalculatedEvent,
        );

        const fundingType = eligibilityCalculatedEvent.Eligibility.FundingType;
        const benefitName = benefitsMap[fundingType]?.benefitName;

        if (
          eligibilityCalculatedEvent.Eligibility.EligibilityStatus ===
            'eligible' &&
          benefitName
        ) {
          dispatch(
            newToast({
              title: `Eligibility calculated for ${benefitName}`,
              description: `We think you are eligible for this benefit`,
              type: 'success',
            }),
          );
        }

        dispatch(refetchEligibilities(eligibilityCalculatedEvent.CareCircleID));
      },
    },
    {
      eventFilter: (event: Event) =>
        event.eventType.startsWith('OrganiseEntry'),
      handler: (event: Event) => {
        if (isOwnEvent(event.payload)) return;
        console.log(`[useEventStream] received ${event.eventType}`);
        const calendarId = parseCalendarID(event.payload);
        if (calendarId) {
          dispatch(
            organiseApi.util.invalidateTags([
              { type: 'Organise', id: calendarId },
            ]),
          );
        }
      },
    },
    {
      eventFilter: (event: Event) =>
        event.eventType === 'OrganiseDocumentScannedV1' ||
        event.eventType === 'OrganiseDocumentScanRequestedV1',
      handler: (event: Event) => {
        console.log(
          `[useEventStream] received ${event.eventType}`,
          event.payload,
        );
        const careCircleId = parseCareCircleID(event.payload);
        if (careCircleId) {
          dispatch(
            organiseDocumentApi.util.invalidateTags([
              { type: 'OrganiseDocument', id: careCircleId },
            ]),
          );
        }
      },
    },
    {
      eventFilter: (event: Event) =>
        event.eventType === 'NotificationCreatedV1',
      handler: (event: Event) => {
        console.log('[useEventStream] received NotificationCreatedV1');
        dispatch(invalidateNotificationTags());

        let title = 'New notification';
        let description: string | undefined;
        let deepLink: string | undefined;
        let notificationID: string | undefined;
        if (typeof event.payload === 'string') {
          try {
            const parsed = JSON.parse(event.payload);
            if (parsed.title) title = parsed.title;
            if (parsed.body) description = parsed.body;
            if (parsed.deepLink) deepLink = parsed.deepLink;
            if (parsed.notificationID) notificationID = parsed.notificationID;
          } catch (e) {
            Sentry.captureException(e, {
              extra: { payload: event.payload },
            });
          }
        }
        dispatch(
          newToast({
            title,
            description,
            type: 'info',
            action: deepLink ? { label: 'View', href: deepLink } : undefined,
            onAction: notificationID
              ? () => {
                  dispatch(
                    notificationApi.endpoints.updateNotification.initiate({
                      id: notificationID!,
                      status: 'read',
                    }),
                  );
                }
              : undefined,
          }),
        );
      },
    },
    {
      eventFilter: (event: Event) =>
        event.eventType === 'ShortlistItemSecuredV2' ||
        event.eventType === 'ShortlistSecuredElsewhereV2' ||
        event.eventType === 'ShortlistPublishedV2',
      handler: (event: Event) => {
        console.log(`[useEventStream] received ${event.eventType}`);
        dispatch(invalidateShortlistTags());
      },
    },
    {
      eventFilter: (event: Event) =>
        event.eventType.startsWith('ChatSession') ||
        event.eventType.startsWith('ChatMessage'),
      handler: (event: Event) => {
        console.log(`[useEventStream] received ${event.eventType}`);
        dispatch(invalidateChatSessionTags());
      },
    },
    {
      eventFilter: (event: Event) =>
        event.eventType === 'InferenceExtractionCompletedEventV1' ||
        event.eventType === 'InferenceFactsConfirmedEventV1' ||
        event.eventType === 'InferenceFactsDismissedEventV1',
      handler: (event: Event) => {
        if (!isCurrentCareCircle(event.payload)) return;
        console.log(`[useEventStream] received ${event.eventType}`);
        const careCircleId = parseCareCircleID(event.payload);
        if (careCircleId) {
          dispatch(
            inferenceApi.util.invalidateTags([
              { type: 'InferenceExtraction', id: careCircleId },
            ]),
          );
          if (event.eventType === 'InferenceExtractionCompletedEventV1') {
            dispatch(
              organiseDocumentApi.util.invalidateTags([
                { type: 'OrganiseDocument', id: careCircleId },
              ]),
            );
            const documentId = parseDocumentID(event.payload);
            if (documentId) dispatch(documentProcessed(documentId));
            const messageId = parseMessageID(event.payload);
            if (messageId) dispatch(messageProcessed(messageId));
          }
        }
      },
    },
    {
      eventFilter: (event: Event) =>
        event.eventType === 'IntakeSummaryReadyEventV1' ||
        event.eventType === 'IntakeSummaryFailedEventV1' ||
        event.eventType === 'IntakeSummaryConfirmedEventV1',
      handler: (event: Event) => {
        if (!isCurrentCareCircle(event.payload)) return;
        console.log(`[useEventStream] received ${event.eventType}`);
        const careCircleId = parseCareCircleID(event.payload);
        if (careCircleId) {
          dispatch(
            inferenceApi.util.invalidateTags([
              { type: 'IntakeSummary', id: careCircleId },
            ]),
          );
        }
      },
    },
    {
      eventFilter: (event: Event) =>
        event.eventType.startsWith(BACKTEST_EVENT_PREFIX),
      handler: (event: Event) => {
        console.log(`[useEventStream] received ${event.eventType}`);
        dispatch(invalidateBacktestTags(parseBacktestID(event.payload)));
      },
    },
  ];

  const processEvent = (data: string): void => {
    const event = JSON.parse(data);
    for (const handler of handlers) {
      if (handler.eventFilter(event)) {
        handler.handler(event);
      }
    }
  };

  // Events missed while disconnected are not replayed, so refetch everything.
  const refetchStreamedData = (): void => {
    dispatch(api.util.invalidateTags(RECONNECT_REFETCHED_TAGS));
    const careCircleID = store.getState().careCircle.currentCareCircle?.id;
    if (careCircleID) dispatch(refetchEligibilities(careCircleID));
  };

  // Reports the first failure in a run; later ones are breadcrumbs.
  const reportTokenError = (err: unknown): void => {
    if (tokenErrorReportedRef.current) {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'getToken failed again — the event stream will retry',
        level: 'warning',
      });
      return;
    }

    tokenErrorReportedRef.current = true;
    Sentry.captureException(err);
  };

  // Asks for a token every time, so a reconnect is never refused as expired.
  const connect = useCallback(async () => {
    const getToken = getTokenRef.current;
    if (!getToken) return;

    // Abort any existing connection
    if (abortRef.current) {
      abortRef.current.abort();
    }

    // Clear any pending reconnection attempts
    if (reconnectTimeoutRef.current) {
      clearTimeout(reconnectTimeoutRef.current);
      reconnectTimeoutRef.current = null;
    }

    const controller = new AbortController();
    abortRef.current = controller;

    let jwt: string | undefined;
    try {
      jwt = await getToken();
      tokenErrorReportedRef.current = false;
    } catch (err) {
      reportTokenError(err);
      scheduleReconnect(controller.signal);
      return;
    }
    if (controller.signal.aborted) return;
    if (!jwt) {
      Sentry.addBreadcrumb({
        category: 'auth',
        message: 'getToken returned empty — the event stream will retry',
        level: 'warning',
      });
      scheduleReconnect(controller.signal);
      return;
    }

    const connectionUrl = `${apiUrl}/v1/events`;
    console.log('[useEventStream] Connecting to SSE');
    connectedCareCircleIDsRef.current = careCircleIDsOf(
      store.getState().careCircle.accountCareCircles,
    );

    fetch(connectionUrl, {
      headers: {
        Authorization: `Bearer ${jwt}`,
      },
      signal: controller.signal,
    })
      .then(async (response) => {
        if (!response.ok) {
          throw new Error(`SSE connection failed: ${response.status}`);
        }

        const reader = response.body!.getReader();
        const decoder = new TextDecoder();
        let buffer = '';
        // Kept across reads, so a frame split between two is not dropped.
        let currentData = '';

        const isReconnect =
          retryCountRef.current > 0 || refetchOnConnectRef.current;
        retryCountRef.current = 0;
        refetchOnConnectRef.current = false;
        console.log('[useEventStream] SSE connected');
        if (isReconnect) refetchStreamedData();

        // eslint-disable-next-line no-constant-condition
        while (true) {
          const { done, value } = await reader.read();
          if (done) break;

          buffer += decoder.decode(value, { stream: true });
          const lines = buffer.split('\n');
          buffer = lines.pop()!;

          for (const line of lines) {
            if (line.startsWith('data: ')) {
              currentData += line.slice(6);
            } else if (line === '' && currentData) {
              try {
                processEvent(currentData);
              } catch (e) {
                console.error('[useEventStream] Error parsing SSE data:', e);
              }
              currentData = '';
            }
            // Lines starting with ':' are comments (heartbeats) — ignore
          }
        }
      })
      .catch((err) => {
        if (err.name === 'AbortError') return;
        console.error('[useEventStream] SSE error:', err);
      })
      .then(() => scheduleReconnect(controller.signal));
  }, []);

  const scheduleReconnect = useCallback((signal: AbortSignal) => {
    // Reconnect with exponential backoff (unless intentionally closed)
    if (!getTokenRef.current || signal.aborted) return;

    const delay = Math.min(
      INITIAL_RETRY_DELAY_MS *
        Math.pow(BACKOFF_MULTIPLIER, retryCountRef.current),
      MAX_RETRY_DELAY_MS,
    );

    retryCountRef.current += 1;
    console.log(
      `[useEventStream] Reconnecting in ${delay}ms (attempt ${retryCountRef.current})`,
    );

    reconnectTimeoutRef.current = setTimeout(() => {
      if (getTokenRef.current) connect();
    }, delay);
  }, []);

  // The API sends a care circle's events only to a stream that connected once
  // the account was in it, so a care circle created or joined since needs a
  // new connection.
  const careCircleIDs = useSelector((state) =>
    careCircleIDsOf(state.careCircle.accountCareCircles),
  );
  useEffect(() => {
    // Not loaded yet, or a refetch failed: nothing to compare against.
    if (careCircleIDs === null) return;
    if (connectedCareCircleIDsRef.current === null) {
      // The stream connected before they loaded, and the API read the same
      // memberships then, so loading them first changes nothing.
      connectedCareCircleIDsRef.current = careCircleIDs;
      return;
    }
    if (careCircleIDs === connectedCareCircleIDsRef.current) return;
    if (!getTokenRef.current) return;
    refetchOnConnectRef.current = true;
    connect();
  }, [careCircleIDs, connect]);

  const startEventStream = useCallback(
    ({ getToken }: { getToken: TokenGetter }) => {
      getTokenRef.current = getToken;
      retryCountRef.current = 0;
      tokenErrorReportedRef.current = false;

      connect();

      // Return cleanup function
      return () => {
        if (reconnectTimeoutRef.current) {
          clearTimeout(reconnectTimeoutRef.current);
          reconnectTimeoutRef.current = null;
        }

        getTokenRef.current = null;

        if (abortRef.current) {
          abortRef.current.abort();
          abortRef.current = null;
        }
      };
    },
    [connect],
  );

  return {
    startEventStream,
    processEvent,
  };
};
